import type { ReactNode } from "react";
import clsx from "clsx";

export type TabItem = {
  id: string;
  label: ReactNode;
  badge?: ReactNode;
};

export function Tabs({
  items,
  activeId,
  onChange,
  "aria-label": ariaLabel,
}: {
  items: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  "aria-label": string;
}) {
  return (
    // `overflow-x-auto` + `whitespace-nowrap`/`shrink-0` on each tab (rather than
    // letting labels wrap) is the fix for the 375px header overflow: a long
    // label like "Design your own" would otherwise wrap to two lines inside
    // its pill and blow out the header's height. Below the width where all
    // tabs fit, this bar scrolls horizontally instead — the page itself never
    // gains a horizontal scrollbar (§17/§20 acceptance item 10).
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="inline-flex max-w-full gap-1 overflow-x-auto rounded-lg border border-border bg-paper-dim p-1"
    >
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <button
            key={item.id}
            role="tab"
            aria-selected={active}
            className={clsx(
              "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              active ? "bg-paper text-ink shadow-sm" : "text-ink-soft hover:text-ink",
            )}
            onClick={() => onChange(item.id)}
          >
            {item.label}
            {item.badge}
          </button>
        );
      })}
    </div>
  );
}
